/**
 * BlockTimeModal.tsx
 *
 * Block-time create/edit modal — full legacy parity with
 * `services/scheduler_block_details.php` + `block_appt_processor.php`.
 *
 * Visually mirrors `UnifiedAppointmentModal` so calendar-side flows feel
 * identical. Sections, top-to-bottom:
 *   - Header strip (Available / Not-Available badge + color swatch)
 *   - Validation banner
 *   - Details (date / duration / start / end, then facility / group /
 *     type / title, then color picker row)
 *   - Availability (Availability + Required selects)
 *   - Resources (legacy required ≥1)
 *   - Notes (`scheduledAppointmentReason`)
 *   - Recurring (days-of-week pills, timeframe radio,
 *     allowances + exceptions)
 *   - Block History (edit only)
 *
 * Footer:
 *   - New: Block Time
 *   - Edit: Delete All Blocks + Convert to Appointment + Save
 *   - Edit + recurring + occurrence: Delete This Block +
 *     Delete All Blocks + Convert to Appointment +
 *     Convert to Single Block + Save
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@/lib/zodResolver';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Plus, Trash2, X } from 'lucide-react';

import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Alert } from '@/components/ui/Alert';
import { Input } from '@/components/ui/Input';
import { TimePicker } from '@/components/ui/TimePicker';
import { Select } from '@/components/ui/Select';
import { DatePicker } from '@/components/ui/DatePicker';
import { Textarea } from '@/components/ui/Textarea';
import { Checkbox } from '@/components/ui/Checkbox';
import { SectionShell } from '@/components/ui/SectionShell';
import { FieldLockScope } from '@/components/ui/FieldLock';
import { useToast } from '@/hooks/useToast';
import { useAuth } from '@/hooks/useAuth';
import { useRoleTitles } from '@/hooks/useRoleTitles';
import {
  ResourceRowList,
  RESOURCE_TYPE_LABELS,
  RESOURCE_TYPE_TO_ROLE_KEY,
  type ResourceRow,
} from '@/components/scheduling/ResourceRowList';
import { getTimezoneAbbreviation, utcToWallClock } from '@/lib/facilityTimezone';
import { listFacilities, type Facility } from '@/services/admin.service';
import { extractFieldErrors, getErrorMessage } from '@/services/apiClient';
import {
  appointmentsService,
  createBlockTime,
  updateBlockTime,
  deleteBlockTime,
  deleteBlockOccurrence,
  convertBlockOccurrence,
  convertBlockToAppointment,
  getBlockTime,
  getBlockHistory,
  getCalendarSettings as getSchedulerCalendarSettings,
  type BlockHistoryEntry,
  type BlockTimeShowResponse,
  type BlockToAppointmentSeed,
  type CalendarSettings as SchedulerCalendarSettings,
  type CreateBlockTimePayload,
  type AppointmentType,
  type Resource,
} from '@/services/appointments.service';
import type { SelectOption } from '@/types/common.types';

// ─── Constants ────────────────────────────────────────────────────────────────

/** Legacy `scheduler_block_details.php` L265-L275. */
const AVAILABILITY_OPTIONS: SelectOption<1 | 2>[] = [
  { value: 1, label: 'Available Time' },
  { value: 2, label: 'Not Available' },
];

/** Legacy `scheduler_block_details.php` L276-L285. */
const REQUIRED_OPTIONS: SelectOption<0 | 1>[] = [
  { value: 0, label: 'Optional (Warning)' },
  { value: 1, label: 'Required (Error)' },
];

/** Legacy `scheduler_block_details.php` L545-L561. */
const TIMEFRAME_OPTIONS: { value: 1 | 2 | 3; label: string }[] = [
  { value: 1, label: 'No end date' },
  { value: 2, label: 'For N days' },
  { value: 3, label: 'Through date' },
];

const DAY_OPTIONS = [
  { key: 'mon', label: 'Mon' },
  { key: 'tue', label: 'Tue' },
  { key: 'wed', label: 'Wed' },
  { key: 'thu', label: 'Thu' },
  { key: 'fri', label: 'Fri' },
  { key: 'sat', label: 'Sat' },
  { key: 'sun', label: 'Sun' },
] as const;

type DayKey = (typeof DAY_OPTIONS)[number]['key'];

/** Legacy `scheduler_block_details.php` L109-L110 default. */
const DEFAULT_BLOCK_COLOR = '#666666';

// ─── Zod schema ───────────────────────────────────────────────────────────────

const schema = z
  .object({
    title: z.string().trim().min(1, 'Title is required'),
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Select a date'),
    startTime: z.string().min(1, 'Select a start time'),
    endTime: z.string().min(1, 'Select an end time'),
    duration: z.coerce.number().int().positive('Enter a duration').min(1, 'Enter a duration'),
    facilityId: z.number().int().nonnegative().optional(),
    appointmentTypeGroupId: z.number().int().nonnegative().optional(),
    appointmentTypeId: z.number().int().nonnegative().optional(),
    backgroundColor: z
      .string()
      .regex(/^#[0-9a-fA-F]{6}$/i, 'Enter a 6-digit hex color')
      .optional()
      .or(z.literal('')),
    blockAvailability: z.union([z.literal(1), z.literal(2)]),
    required: z.union([z.literal(0), z.literal(1)]),
    notes: z.string().max(4000).optional(),
    resourceIds: z.array(z.number().int().positive()).min(1, 'Select at least one resource'),
    recurring: z.boolean().optional(),
    timeframeType: z.union([z.literal(1), z.literal(2), z.literal(3)]),
    timeframeDuration: z.coerce.number().int().nonnegative().optional(),
    timeframeDate: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, 'Select a date')
      .optional()
      .or(z.literal('')),
    days: z.object({
      mon: z.boolean(),
      tue: z.boolean(),
      wed: z.boolean(),
      thu: z.boolean(),
      fri: z.boolean(),
      sat: z.boolean(),
      sun: z.boolean(),
    }),
    extraDates: z.array(z.string().regex(/^\d{4}-\d{2}-\d{2}$/)),
    exceptionDates: z.array(z.string().regex(/^\d{4}-\d{2}-\d{2}$/)),
  })
  .superRefine((data, ctx) => {
    if (!data.recurring) return;
    if (data.timeframeType === 2 && (data.timeframeDuration ?? 0) <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['timeframeDuration'],
        message: 'Enter a number of days',
      });
    }
    if (data.timeframeType === 3 && !data.timeframeDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['timeframeDate'],
        message: 'Select a through date',
      });
    }
  });

type FormValues = z.infer<typeof schema>;

const SERVER_FIELD_TO_FORM: Record<string, keyof FormValues> = {
  title: 'title',
  scheduledAppointmentName: 'title',
  startTime: 'startTime',
  endTime: 'endTime',
  duration: 'duration',
  facilityId: 'facilityId',
  facilityID: 'facilityId',
  appointmentTypeId: 'appointmentTypeId',
  appointmentTypeID: 'appointmentTypeId',
  appointmentTypeGroupId: 'appointmentTypeGroupId',
  appointmentTypeGroupID: 'appointmentTypeGroupId',
  resourceIds: 'resourceIds',
  resourceIDs: 'resourceIds',
  blockAvailability: 'blockAvailability',
  required: 'required',
  notes: 'notes',
  reason: 'notes',
  scheduledAppointmentReason: 'notes',
  backgroundColor: 'backgroundColor',
  timeframeType: 'timeframeType',
  timeframeDuration: 'timeframeDuration',
  timeframeDate: 'timeframeDate',
};

const FIELD_LABEL: Record<string, string> = {
  title: 'Title',
  date: 'Date',
  startTime: 'Start time',
  endTime: 'End time',
  duration: 'Duration',
  facilityId: 'Facility',
  appointmentTypeGroupId: 'Group',
  appointmentTypeId: 'Type',
  resourceIds: 'Resources',
  blockAvailability: 'Availability',
  required: 'Required',
  backgroundColor: 'Color',
  timeframeType: 'Timeframe',
  timeframeDuration: 'Duration',
  timeframeDate: 'End date',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function toDate(iso: string | null | undefined): string {
  if (!iso) return '';
  // Handle both ISO and `YYYY-MM-DD HH:MM:SS`
  return iso.slice(0, 10);
}

function toTime(iso: string | null | undefined): string {
  if (!iso) return '';
  // `2024-01-02T03:04:05Z` → `03:04`
  return iso.includes('T') ? iso.slice(11, 16) : iso.slice(11, 16);
}

function toIsoZ(date: string, time: string): string {
  return `${date}T${time}:00Z`;
}

function diffMinutes(start: string, end: string): number {
  if (!start || !end) return 0;
  const s = new Date(start).getTime();
  const e = new Date(end).getTime();
  if (Number.isNaN(s) || Number.isNaN(e)) return 0;
  return Math.max(0, Math.round((e - s) / 60000));
}

function addMinutes(date: string, time: string, minutes: number): string {
  if (!date || !time) return '';
  const start = new Date(toIsoZ(date, time));
  if (Number.isNaN(start.getTime())) return '';
  start.setUTCMinutes(start.getUTCMinutes() + minutes);
  const hh = String(start.getUTCHours()).padStart(2, '0');
  const mm = String(start.getUTCMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

// ─── Component ────────────────────────────────────────────────────────────────

export interface BlockTimeModalProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  /** ISO Z datetime — used to seed start when creating from a calendar drag. */
  defaultStart?: string;
  /** ISO Z datetime — used to seed end when creating from a calendar drag. */
  defaultEnd?: string;
  /** When set, the modal loads and edits an existing block instead of creating. */
  editBlockId?: number;
  /** Recurring-block occurrence the user clicked (YYYY-MM-DD). */
  occurrenceDate?: string;
  /** Optional handoff for "Convert Block to Appointment". */
  onConvertToAppointment?: (seed: BlockToAppointmentSeed['seed']) => void;
  /**
   * Show an existing block without letting anything change (a Locked Admin
   * Time Blocks page, plan 2026-10-10-001 R8): every field shows its value as
   * plain, not greyed, content; no add / remove, no Delete / Convert / Save —
   * Close only; and nothing says why (R18). The calendar never sets it.
   */
  readOnly?: boolean;
}

/** "HH:MM" as "h:mm AM" — a time shown as text when the modal is read-only. */
function to12h(hhmm: string | undefined): string {
  const m = /^(\d{1,2}):(\d{2})/.exec(hhmm ?? '');
  if (!m) return hhmm ?? '';
  const h = Number(m[1]);
  return `${h % 12 === 0 ? 12 : h % 12}:${m[2]} ${h < 12 ? 'AM' : 'PM'}`;
}

export default function BlockTimeModal({
  open,
  onClose,
  onSaved,
  defaultStart,
  defaultEnd,
  editBlockId,
  occurrenceDate,
  onConvertToAppointment,
  readOnly = false,
}: BlockTimeModalProps) {
  const qc = useQueryClient();
  const toast = useToast();
  const { user, hasPermission } = useAuth();
  const isEdit = editBlockId != null;
  const canManageBlocks = (user?.userLevel ?? 0) > 2 || hasPermission(9);

  const [topError, setTopError] = useState<string | null>(null);
  const [serverFieldErrors, setServerFieldErrors] = useState<Record<string, string>>({});
  const [extraDateInput, setExtraDateInput] = useState<string>('');
  const [exceptionDateInput, setExceptionDateInput] = useState<string>('');

  // ── Form ───────────────────────────────────────────────────────────────────
  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    getValues,
    reset,
    setError,
    clearErrors,
    formState: { errors, isSubmitting, isSubmitted },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: (() => {
      // Toolbar now seeds defaultStart/defaultEnd in naive wall-clock format
      // ("YYYY-MM-DD HH:MM:SS"). `toDate`/`toTime` work for both that shape
      // and the legacy fake-UTC-Z form (slice 0-10 + slice 11-16 in both
      // cases produces the user's intended wall-clock hour because the
      // legacy toolbar packed the click-hour integer directly into the
      // "...HH:00:00Z" string). The earlier `utcToWallClock(_, PACIFIC)`
      // wrapper here was the bug — it projected onto Pacific regardless
      // of the account/facility TZ. Edit-mode (API returns a true UTC
      // ISO) needs explicit reprojection — handled in the hydration
      // effect below — but create-mode does not.
      return {
      title: '',
      date: toDate(defaultStart) || new Date().toISOString().slice(0, 10),
      startTime: toTime(defaultStart) || '09:00',
      endTime: toTime(defaultEnd) || '10:00',
      duration: defaultStart && defaultEnd ? diffMinutes(defaultStart, defaultEnd) || 60 : 60,
      facilityId: 0,
      appointmentTypeGroupId: 0,
      appointmentTypeId: 0,
      backgroundColor: DEFAULT_BLOCK_COLOR,
      blockAvailability: 2,
      required: 0,
      notes: '',
      resourceIds: [],
      recurring: false,
      timeframeType: 1,
      timeframeDuration: 0,
      timeframeDate: '',
      days: { mon: false, tue: false, wed: false, thu: false, fri: false, sat: false, sun: false },
      extraDates: [],
      exceptionDates: [],
    };
    })(),
  });

  const watchedDate = watch('date');
  const watchedStart = watch('startTime');
  const watchedDuration = watch('duration');
  const watchedAvailability = watch('blockAvailability');
  const watchedColor = watch('backgroundColor') || DEFAULT_BLOCK_COLOR;
  const watchedTypeId = watch('appointmentTypeId');
  const watchedGroupId = watch('appointmentTypeGroupId');
  const watchedRecurring = watch('recurring');
  const watchedTimeframeType = watch('timeframeType');
  const watchedExtras = watch('extraDates');
  const watchedExceptions = watch('exceptionDates');

  // ── Lookup data ────────────────────────────────────────────────────────────
  const refsQuery = useQuery({
    queryKey: ['block-time-refs'],
    enabled: open,
    queryFn: async () => {
      const [types, resources, facilities] = await Promise.all([
        appointmentsService.getAppointmentTypes(),
        appointmentsService.getResources(),
        listFacilities(),
      ]);
      return { types, resources, facilities };
    },
  });
  const types: AppointmentType[] = refsQuery.data?.types ?? [];
  const resources: Resource[] = refsQuery.data?.resources ?? [];
  const facilities: Facility[] = refsQuery.data?.facilities ?? [];

  // Mirror UnifiedAppointmentModal's TZ resolution so blocks display in the
  // facility/account timezone instead of being hardcoded to Pacific. The
  // SchedulePage already runs this same query — react-query's cache will
  // return immediately on a hit.
  const schedulerSettingsQuery = useQuery<SchedulerCalendarSettings>({
    queryKey: ['scheduler-calendar-settings'],
    queryFn: getSchedulerCalendarSettings,
    enabled: open,
    staleTime: 10 * 60 * 1000,
  });
  const accountIanaTimezone = schedulerSettingsQuery.data?.accountIanaTimezone
    ?? 'America/Los_Angeles';

  // displayTz: facility IANA when a facility is selected on the form,
  // otherwise the account default. Matches UnifiedAppointmentModal
  // L2263-L2272 verbatim.
  const watchedFacilityId = watch('facilityId');
  const displayTz = useMemo(() => {
    if (watchedFacilityId) {
      const facTz = facilities.find((f) => f.facilityId === watchedFacilityId)?.ianaTimezone;
      if (facTz) return facTz;
    }
    return accountIanaTimezone;
  }, [watchedFacilityId, facilities, accountIanaTimezone]);
  const tzAbbreviation = useMemo(() => getTimezoneAbbreviation(displayTz), [displayTz]);

  // ── Resource row-list state (mirrors UnifiedAppointmentModal) ─────────────
  const { titles: roleTitleOverrides } = useRoleTitles();
  const resourceTypeLabels = useMemo<Record<string, string>>(() => {
    const merged: Record<string, string> = { ...RESOURCE_TYPE_LABELS };
    for (const [singular, roleKey] of Object.entries(RESOURCE_TYPE_TO_ROLE_KEY)) {
      const override = roleTitleOverrides[roleKey];
      if (override && override.trim().length > 0) merged[singular] = override;
    }
    return merged;
  }, [roleTitleOverrides]);

  const [selectedResourceIds, setSelectedResourceIds] = useState<Set<number>>(new Set());
  const [rowDrafts, setRowDrafts] = useState<
    Array<{ key: string; resourceType: Resource['resourceType'] }>
  >([]);

  const resourceRowListRows: ResourceRow[] = useMemo(() => {
    const committed: ResourceRow[] = [...selectedResourceIds].map((id) => {
      const found = resources.find((r) => r.resourceId === id);
      return {
        key: `committed-${id}`,
        resourceType: (found?.resourceType ?? 'other') as Resource['resourceType'],
        resourceId: id,
      };
    });
    const drafts: ResourceRow[] = rowDrafts.map((d) => ({
      key: d.key,
      resourceType: d.resourceType,
      resourceId: null,
    }));
    return [...committed, ...drafts];
  }, [selectedResourceIds, resources, rowDrafts]);

  useEffect(() => {
    setValue('resourceIds', [...selectedResourceIds], isSubmitted ? { shouldValidate: true } : undefined);
  }, [selectedResourceIds, setValue, isSubmitted]);

  useEffect(() => {
    if (!open) {
      setSelectedResourceIds(new Set());
      setRowDrafts([]);
    }
  }, [open]);

  const handleAddResourceRow = useCallback(() => {
    setRowDrafts((prev) => [
      ...prev,
      { key: `draft-${Date.now()}-${prev.length}`, resourceType: 'doctor' },
    ]);
  }, []);

  const handleChangeRowType = useCallback(
    (rowKey: string, nextType: Resource['resourceType']) => {
      if (rowKey.startsWith('draft-')) {
        setRowDrafts((prev) =>
          prev.map((d) => (d.key === rowKey ? { ...d, resourceType: nextType } : d)),
        );
      } else {
        const idStr = rowKey.replace('committed-', '');
        const oldId = Number(idStr);
        if (Number.isFinite(oldId)) {
          setSelectedResourceIds((prev) => {
            const next = new Set(prev);
            next.delete(oldId);
            return next;
          });
          setRowDrafts((prev) => [
            ...prev,
            { key: `draft-${Date.now()}-${prev.length}`, resourceType: nextType },
          ]);
        }
      }
    },
    [],
  );

  const handleChangeRowResource = useCallback(
    (rowKey: string, nextResourceId: number | null) => {
      if (rowKey.startsWith('draft-')) {
        setRowDrafts((prev) => prev.filter((d) => d.key !== rowKey));
        if (nextResourceId != null) {
          setSelectedResourceIds((prev) => {
            const nextSet = new Set(prev);
            nextSet.add(nextResourceId);
            return nextSet;
          });
        }
        return;
      }
      const idStr = rowKey.replace('committed-', '');
      const oldId = Number(idStr);
      setSelectedResourceIds((prev) => {
        const nextSet = new Set(prev);
        if (Number.isFinite(oldId)) nextSet.delete(oldId);
        if (nextResourceId != null) nextSet.add(nextResourceId);
        return nextSet;
      });
    },
    [],
  );

  // Bulk pick from a draft row: the row itself goes away and each chosen
  // resource becomes its own committed row, exactly as a one-at-a-time pick
  // would have produced. Set semantics keep a resource staged twice (two draft
  // rows open at once) from landing twice.
  const handleAddResources = useCallback((rowKey: string, ids: number[]) => {
    setRowDrafts((prev) => prev.filter((d) => d.key !== rowKey));
    setSelectedResourceIds((prev) => {
      const next = new Set(prev);
      for (const id of ids) {
        if (Number.isFinite(id) && id > 0) next.add(id);
      }
      return next;
    });
  }, []);

  const handleRemoveRow = useCallback((rowKey: string) => {
    if (rowKey.startsWith('draft-')) {
      setRowDrafts((prev) => prev.filter((d) => d.key !== rowKey));
      return;
    }
    const idStr = rowKey.replace('committed-', '');
    const oldId = Number(idStr);
    if (Number.isFinite(oldId)) {
      setSelectedResourceIds((prev) => {
        const next = new Set(prev);
        next.delete(oldId);
        return next;
      });
    }
  }, []);

  // ── Existing block (edit) ─────────────────────────────────────────────────
  const blockQuery = useQuery({
    queryKey: ['block-time', editBlockId],
    enabled: open && isEdit,
    queryFn: () => getBlockTime(editBlockId as number),
  });

  const historyQuery = useQuery({
    queryKey: ['block-time-history', editBlockId],
    enabled: open && isEdit,
    queryFn: () => getBlockHistory(editBlockId as number),
  });

  // ── Hydrate from edit fetch ────────────────────────────────────────────────
  useEffect(() => {
    if (!isEdit || !blockQuery.data) return;
    const data: BlockTimeShowResponse = blockQuery.data;
    const blk = data.block;
    const recur = data.recurrence;
    // The API returns startTime/endTime as a true UTC ISO `Z`. Project into
    // the block's facility timezone (or account default when no facility),
    // mirroring UnifiedAppointmentModal L1955-L1975. The previous hardcoded
    // Pacific projection produced the wrong wall-clock for every non-Pacific
    // account — and when the user then saved without correcting it, the
    // naive string was reinterpreted in the account TZ by the backend and
    // the row landed at a different real-world instant from what was shown.
    const editFacTz = blk.facilityId
      ? (facilities.find((f) => f.facilityId === blk.facilityId)?.ianaTimezone
        ?? accountIanaTimezone)
      : accountIanaTimezone;
    const startWall = utcToWallClock(blk.startTime, editFacTz)
      ?? { date: toDate(blk.startTime), time: toTime(blk.startTime) };
    const endWall = utcToWallClock(blk.endTime, editFacTz)
      ?? { date: toDate(blk.endTime), time: toTime(blk.endTime) };
    reset({
      title: blk.title || '',
      date: startWall.date || new Date().toISOString().slice(0, 10),
      startTime: startWall.time || '09:00',
      endTime: endWall.time || '10:00',
      duration: blk.duration || diffMinutes(blk.startTime, blk.endTime) || 60,
      facilityId: blk.facilityId ?? 0,
      appointmentTypeGroupId: blk.appointmentTypeGroupId ?? 0,
      appointmentTypeId: blk.appointmentTypeId ?? 0,
      backgroundColor: blk.backgroundColor || DEFAULT_BLOCK_COLOR,
      blockAvailability: blk.blockAvailability ?? 2,
      required: blk.required ? 1 : 0,
      notes: blk.notes ?? '',
      resourceIds: blk.resourceIds ?? [],
      recurring: recur?.recurring ?? false,
      timeframeType: (recur?.timeframeType ?? 1) as 1 | 2 | 3,
      timeframeDuration: recur?.timeframeDuration ?? 0,
      timeframeDate: recur?.timeframeDate && recur.timeframeDate !== '0000-00-00'
        ? recur.timeframeDate
        : '',
      days: {
        mon: !!recur?.mon,
        tue: !!recur?.tue,
        wed: !!recur?.wed,
        thu: !!recur?.thu,
        fri: !!recur?.fri,
        sat: !!recur?.sat,
        sun: !!recur?.sun,
      },
      extraDates: data.allowances ?? [],
      exceptionDates: data.exceptions ?? [],
    });
    setSelectedResourceIds(new Set(blk.resourceIds ?? []));
    setRowDrafts([]);
  }, [isEdit, blockQuery.data, reset, facilities, accountIanaTimezone]);

  // ── End-time = start + duration (legacy `appt_duration` calc) ──────────────
  // `watchedEnd` is deliberately NOT a dependency: the effect keeps end slaved
  // to start + duration, but must not re-fire (and clobber) when the user picks
  // an end time directly — that path recomputes duration instead (see below).
  useEffect(() => {
    if (!watchedDate || !watchedStart || !watchedDuration) return;
    const computed = addMinutes(watchedDate, watchedStart, watchedDuration);
    if (computed) {
      setValue('endTime', computed, { shouldDirty: false, shouldValidate: false });
      // A recompute always yields a consistent end time, so clear any stale
      // "end before start" error left over from a rejected manual pick.
      clearErrors('endTime');
    }
  }, [watchedDate, watchedStart, watchedDuration, setValue, clearErrors]);

  // ── Type → background color + duration prefill (legacy `loadBlockTypeDefaults`,
  //    calendar-test.php L1317-L1340) ─────────────────────────────────────────
  useEffect(() => {
    const t = types.find((x) => x.appointmentTypeId === watchedTypeId);
    if (!t) return;
    if (t.appointmentTypeDuration && (!getValues('duration') || getValues('duration') === 60)) {
      setValue('duration', t.appointmentTypeDuration, { shouldDirty: true });
    }
    const currentColor = (getValues('backgroundColor') || '').trim();
    if (
      t.appointmentTypeColor &&
      (!currentColor || currentColor.toLowerCase() === DEFAULT_BLOCK_COLOR)
    ) {
      setValue('backgroundColor', t.appointmentTypeColor, { shouldDirty: true });
    }
    if (t.appointmentTypeGroupId && t.appointmentTypeGroupId !== watchedGroupId) {
      setValue('appointmentTypeGroupId', t.appointmentTypeGroupId, { shouldDirty: true });
    }
  }, [watchedTypeId, types, getValues, setValue, watchedGroupId]);

  // ── Group / Type / Facility option lists ───────────────────────────────────
  const groupOptions = useMemo<SelectOption<number>[]>(() => {
    const seen = new Map<number, string>();
    types.forEach((t) => {
      if (t.appointmentTypeGroupId && !seen.has(t.appointmentTypeGroupId)) {
        seen.set(
          t.appointmentTypeGroupId,
          t.appointmentTypeGroupName ?? `Group #${t.appointmentTypeGroupId}`
        );
      }
    });
    return [
      { value: 0, label: '— Select Group —' },
      ...Array.from(seen.entries()).map(([id, name]) => ({ value: id, label: name })),
    ];
  }, [types]);

  const typeOptions = useMemo<SelectOption<number>[]>(() => {
    const filtered = watchedGroupId
      ? types.filter((t) => t.appointmentTypeGroupId === watchedGroupId)
      : types;
    return [
      { value: 0, label: '— Select Type —' },
      ...filtered.map((t) => ({
        value: t.appointmentTypeId,
        label: t.appointmentTypeName,
      })),
    ];
  }, [types, watchedGroupId]);

  const facilityOptions = useMemo<SelectOption<number>[]>(() => {
    return [
      { value: 0, label: '— Select Facility —' },
      ...facilities.filter((f) => f.active).map((f) => ({ value: f.facilityId, label: f.facilityName })),
    ];
  }, [facilities]);

  // ── Mutations ──────────────────────────────────────────────────────────────
  const buildPayload = useCallback(
    (values: FormValues): CreateBlockTimePayload => {
      // Send naive "YYYY-MM-DD HH:MM:SS" strings (no `T`, no `Z`) so the
      // backend's normalizeDateTime parses them in the facility/account
      // timezone via `Carbon::parse($value, $sourceTz)`. The previous
      // toIsoZ() format appended `Z`, which made Carbon treat the time
      // as UTC regardless of $sourceTz — saved values came back several
      // hours off (the user's UTC offset). Matches the appointment
      // modal's wire format at UnifiedAppointmentModal.tsx L2965-L2966.
      const startNaive = `${values.date} ${values.startTime}:00`;
      const endNaive = `${values.date} ${values.endTime}:00`;
      return {
        title: values.title.trim(),
        startTime: startNaive,
        endTime: endNaive,
        date: values.date,
        duration: values.duration,
        resourceIds: values.resourceIds,
        facilityId: values.facilityId || undefined,
        appointmentTypeId: values.appointmentTypeId || undefined,
        appointmentTypeGroupId: values.appointmentTypeGroupId || undefined,
        backgroundColor: values.backgroundColor || undefined,
        blockAvailability: values.blockAvailability,
        required: values.required,
        notes: values.notes ?? '',
        recurring: values.recurring ? 1 : 0,
        timeframeType: values.timeframeType,
        timeframeDuration: values.timeframeDuration ?? 0,
        timeframeDate: values.timeframeType === 3 && values.timeframeDate
          ? values.timeframeDate
          : undefined,
        daysOfWeek: {
          mon: values.days.mon ? 1 : 0,
          tue: values.days.tue ? 1 : 0,
          wed: values.days.wed ? 1 : 0,
          thu: values.days.thu ? 1 : 0,
          fri: values.days.fri ? 1 : 0,
          sat: values.days.sat ? 1 : 0,
          sun: values.days.sun ? 1 : 0,
        },
        extraDates: values.extraDates,
        exceptionDates: values.exceptionDates,
      };
    },
    []
  );

  const createMutation = useMutation({
    mutationFn: (values: FormValues) => createBlockTime(buildPayload(values)),
    onSuccess: () => {
      toast.success('Block time created');
      void qc.invalidateQueries({ queryKey: ['schedule'] });
      onSaved();
    },
    onError: (err) => mapServerError(err, 'Failed to create block'),
  });

  const updateMutation = useMutation({
    mutationFn: (values: FormValues) =>
      updateBlockTime(editBlockId as number, buildPayload(values)),
    onSuccess: () => {
      toast.success('Block updated');
      void qc.invalidateQueries({ queryKey: ['schedule'] });
      void qc.invalidateQueries({ queryKey: ['block-time', editBlockId] });
      void qc.invalidateQueries({ queryKey: ['block-time-history', editBlockId] });
      onSaved();
    },
    onError: (err) => mapServerError(err, 'Failed to update block'),
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteBlockTime(editBlockId as number),
    onSuccess: () => {
      toast.success('Block series deleted');
      void qc.invalidateQueries({ queryKey: ['schedule'] });
      onSaved();
    },
    onError: (err) => mapServerError(err, 'Failed to delete block'),
  });

  const deleteOccurrenceMutation = useMutation({
    mutationFn: (date: string) => deleteBlockOccurrence(editBlockId as number, date),
    onSuccess: () => {
      toast.success('Occurrence removed');
      void qc.invalidateQueries({ queryKey: ['schedule'] });
      onSaved();
    },
    onError: (err) => mapServerError(err, 'Failed to remove occurrence'),
  });

  const convertOccurrenceMutation = useMutation({
    mutationFn: (date: string) => convertBlockOccurrence(editBlockId as number, date),
    onSuccess: () => {
      toast.success('Occurrence split into single-date block');
      void qc.invalidateQueries({ queryKey: ['schedule'] });
      onSaved();
    },
    onError: (err) => mapServerError(err, 'Failed to convert occurrence'),
  });

  const convertToAppointmentMutation = useMutation({
    mutationFn: () => convertBlockToAppointment(editBlockId as number),
    onSuccess: (data) => {
      onConvertToAppointment?.(data.seed);
      onClose();
    },
    onError: (err) => mapServerError(err, 'Failed to start appointment conversion'),
  });

  function mapServerError(err: unknown, fallback: string) {
    setTopError(getErrorMessage(err) || fallback);
    setServerFieldErrors(extractFieldErrors(err) ?? {});
  }

  // ── Submit ─────────────────────────────────────────────────────────────────
  function onSubmit(values: FormValues) {
    setTopError(null);
    setServerFieldErrors({});
    if (isEdit) {
      updateMutation.mutate(values);
    } else {
      createMutation.mutate(values);
    }
  }

  function onInvalid() {
    setTopError('Please fix the highlighted fields and try again.');
  }

  // ── Allowance / exception helpers ──────────────────────────────────────────
  function addExtraDate() {
    const v = extraDateInput.trim();
    if (!v) return;
    const next = Array.from(new Set([...(watchedExtras ?? []), v])).sort();
    setValue('extraDates', next, { shouldDirty: true });
    setExtraDateInput('');
  }
  function removeExtraDate(date: string) {
    setValue(
      'extraDates',
      (watchedExtras ?? []).filter((d) => d !== date),
      { shouldDirty: true }
    );
  }
  function addExceptionDate() {
    const v = exceptionDateInput.trim();
    if (!v) return;
    const next = Array.from(new Set([...(watchedExceptions ?? []), v])).sort();
    setValue('exceptionDates', next, { shouldDirty: true });
    setExceptionDateInput('');
  }
  function removeExceptionDate(date: string) {
    setValue(
      'exceptionDates',
      (watchedExceptions ?? []).filter((d) => d !== date),
      { shouldDirty: true }
    );
  }

  // ── Validation banner ──────────────────────────────────────────────────────
  const aggregatedErrors = useMemo(() => {
    const list: { field: string; label: string; message: string }[] = [];
    Object.entries(errors).forEach(([key, val]) => {
      if (val && typeof val === 'object' && 'message' in val) {
        list.push({
          field: key,
          label: FIELD_LABEL[key] ?? key,
          message: String((val as { message?: string }).message ?? 'Invalid'),
        });
      }
    });
    Object.entries(serverFieldErrors).forEach(([key, msg]) => {
      const formField = SERVER_FIELD_TO_FORM[key] ?? (key as keyof FormValues);
      list.push({
        field: formField as string,
        label: FIELD_LABEL[formField as string] ?? key,
        message: msg,
      });
    });
    return list;
  }, [errors, serverFieldErrors]);

  // ── Footer ─────────────────────────────────────────────────────────────────
  const footer = readOnly ? (
    <div className="flex w-full justify-end">
      <Button type="button" variant="secondary" onClick={onClose}>
        Close
      </Button>
    </div>
  ) : (
    <div className="flex flex-wrap items-center gap-2 w-full">
      {isEdit && canManageBlocks && watchedRecurring && occurrenceDate && (
        <Button
          type="button"
          variant="secondary"
          onClick={() => deleteOccurrenceMutation.mutate(occurrenceDate)}
          disabled={deleteOccurrenceMutation.isPending}
        >
          Delete This Block
        </Button>
      )}
      {isEdit && canManageBlocks && (
        <Button
          type="button"
          variant="danger"
          onClick={() => {
            if (confirm('Delete this block series? All occurrences will be removed.')) {
              deleteMutation.mutate();
            }
          }}
          disabled={deleteMutation.isPending}
        >
          <Trash2 className="h-4 w-4 mr-1" />
          Delete All Blocks
        </Button>
      )}
      {isEdit && canManageBlocks && (
        <Button
          type="button"
          variant="secondary"
          onClick={() => convertToAppointmentMutation.mutate()}
          disabled={convertToAppointmentMutation.isPending}
        >
          Convert to Appointment
        </Button>
      )}
      {isEdit && canManageBlocks && watchedRecurring && occurrenceDate && (
        <Button
          type="button"
          variant="secondary"
          onClick={() => convertOccurrenceMutation.mutate(occurrenceDate)}
          disabled={convertOccurrenceMutation.isPending}
        >
          Convert to Single Block
        </Button>
      )}
      <div className="ml-auto">
        <Button
          type="button"
          onClick={handleSubmit(onSubmit, onInvalid)}
          disabled={!canManageBlocks || isSubmitting || createMutation.isPending || updateMutation.isPending}
        >
          {isEdit ? 'Save' : 'Block Time'}
        </Button>
      </div>
    </div>
  );

  return (
    <Modal isOpen={open} onClose={onClose} title={readOnly ? 'Time Block' : isEdit ? 'Edit Block' : 'Block Time'} size="xl" footer={footer}>
      {/* Header strip — Available / Not-Available badge + color swatch */}
      <div className="flex items-center gap-3 px-1 mb-4">
        <Badge variant={watchedAvailability === 1 ? 'success' : 'warning'}>
          {watchedAvailability === 1 ? 'Available Time Block' : 'Unavailable Time Block'}
        </Badge>
        <div
          className="h-5 w-5 rounded border border-slate-300"
          style={{ backgroundColor: watchedColor }}
          aria-label="Block color preview"
        />
      </div>

      {/* Validation banner */}
      {(topError || aggregatedErrors.length > 0) && (
        <Alert variant="error" className="mb-4">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5" />
            <div className="flex-1">
              <div className="font-semibold">{topError ?? 'Please fix the following:'}</div>
              {aggregatedErrors.length > 0 && (
                <ul className="mt-1 text-xs list-disc list-inside">
                  {aggregatedErrors.map((e, idx) => (
                    <li key={`${e.field}-${idx}`}>
                      <strong>{e.label}:</strong> {e.message}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </Alert>
      )}

      {!canManageBlocks && !readOnly && (
        <Alert variant="warning" className="mb-4">
          You do not have permission to manage block time. The form is read-only.
        </Alert>
      )}

      <FieldLockScope locked={readOnly}>
      {/* Details */}
      <SectionShell title="Details">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Controller
            control={control}
            name="date"
            render={({ field }) => (
              <DatePicker
                label="Date"
                required={!readOnly}
                value={field.value}
                onChange={field.onChange}
                error={errors.date?.message}
              />
            )}
          />
          <Input
            type="number"
            label="Duration (min)"
            required={!readOnly}
            min={1}
            error={errors.duration?.message}
            {...register('duration', { valueAsNumber: true })}
          />
          <Controller
            control={control}
            name="startTime"
            render={({ field }) =>
              readOnly ? (
                <Input label={`Start Time (${tzAbbreviation})`} value={to12h(field.value)} />
              ) : (
                <TimePicker
                  label={`Start Time (${tzAbbreviation})`}
                  required
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  error={errors.startTime?.message}
                />
              )
            }
          />
          <Controller
            control={control}
            name="endTime"
            render={({ field }) =>
              readOnly ? (
                <Input label={`End Time (${tzAbbreviation})`} value={to12h(field.value)} />
              ) : (
                <TimePicker
                  label={`End Time (${tzAbbreviation})`}
                  hint="Auto-fills; editable"
                  value={field.value ?? ''}
                  onChange={(next) => {
                    field.onChange(next);
                    if (watchedDate && watchedStart && next) {
                      const dur = diffMinutes(
                        toIsoZ(watchedDate, watchedStart),
                        toIsoZ(watchedDate, next),
                      );
                      if (dur > 0) {
                        clearErrors('endTime');
                        setValue('duration', dur, { shouldValidate: true, shouldDirty: true });
                      } else {
                        setError('endTime', {
                          type: 'manual',
                          message: 'End time must be after start time',
                        });
                      }
                    }
                  }}
                  error={errors.endTime?.message}
                />
              )
            }
          />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
          <Controller
            control={control}
            name="facilityId"
            render={({ field }) => (
              <Select
                label="Facility"
                options={facilityOptions}
                value={field.value ?? 0}
                onChange={(v) => field.onChange(v ?? 0)}
                error={errors.facilityId?.message}
              />
            )}
          />
          <Controller
            control={control}
            name="appointmentTypeGroupId"
            render={({ field }) => (
              <Select
                label="Group"
                options={groupOptions}
                value={field.value ?? 0}
                onChange={(v) => {
                  field.onChange(v ?? 0);
                  setValue('appointmentTypeId', 0, { shouldDirty: true });
                }}
                error={errors.appointmentTypeGroupId?.message}
              />
            )}
          />
          <Controller
            control={control}
            name="appointmentTypeId"
            render={({ field }) => (
              <Select
                label="Type"
                options={typeOptions}
                value={field.value ?? 0}
                onChange={(v) => field.onChange(v ?? 0)}
                error={errors.appointmentTypeId?.message}
              />
            )}
          />
          <Input
            label="Title"
            required={!readOnly}
            error={errors.title?.message}
            {...register('title')}
          />
        </div>

        {/* Color picker row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 items-end">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Color</label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={watchedColor}
                readOnly={readOnly}
                // Read-only: no change, and a click never opens the colour picker.
                onClick={readOnly ? (e) => e.preventDefault() : undefined}
                onChange={readOnly ? undefined : (e) => setValue('backgroundColor', e.target.value, { shouldDirty: true })}
                className={`h-9 w-12 rounded border border-slate-300 p-0 ${readOnly ? 'cursor-default' : 'cursor-pointer'}`}
                aria-label={readOnly ? 'Block color' : 'Pick block color'}
              />
              <Input
                value={watchedColor}
                onChange={(e) => setValue('backgroundColor', e.target.value, { shouldDirty: true })}
                error={errors.backgroundColor?.message}
                wrapperClassName="flex-1"
                placeholder={DEFAULT_BLOCK_COLOR}
              />
            </div>
          </div>
        </div>
      </SectionShell>

      {/* Availability */}
      <SectionShell title="Availability">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Controller
            control={control}
            name="blockAvailability"
            render={({ field }) => (
              <Select<1 | 2>
                label="Availability"
                required={!readOnly}
                options={AVAILABILITY_OPTIONS}
                value={field.value as 1 | 2}
                onChange={(v) => field.onChange((v ?? 2) as 1 | 2)}
                error={errors.blockAvailability?.message}
              />
            )}
          />
          <Controller
            control={control}
            name="required"
            render={({ field }) => (
              <Select<0 | 1>
                label="Required"
                options={REQUIRED_OPTIONS}
                value={field.value as 0 | 1}
                onChange={(v) => field.onChange((v ?? 0) as 0 | 1)}
                error={errors.required?.message}
              />
            )}
          />
        </div>
      </SectionShell>

      {/* Resources */}
      <SectionShell title="People / Resources">
        {refsQuery.isLoading ? (
          <div className="text-sm text-slate-500">Loading resources…</div>
        ) : readOnly ? (
          // Read-only: the block's resources as a list (ResourceRowList only edits).
          selectedResourceIds.size === 0 ? (
            <p className="text-sm text-slate-500">No resources assigned.</p>
          ) : (
            <ul className="space-y-1 text-sm text-slate-700" data-testid="block-resources">
              {[...selectedResourceIds].map((id) => {
                const found = resources.find((r) => r.resourceId === id);
                const type = found ? resourceTypeLabels[found.resourceType] ?? found.resourceType : '';
                return (
                  <li key={id}>
                    {type && <span className="text-slate-500">{type}: </span>}
                    {found?.resourceName ?? `Resource #${id}`}
                  </li>
                );
              })}
            </ul>
          )
        ) : (
          <ResourceRowList
            rows={resourceRowListRows}
            resources={resources}
            resourceTypeLabels={resourceTypeLabels}
            onChangeType={handleChangeRowType}
            onChangeResource={handleChangeRowResource}
            onRemoveRow={handleRemoveRow}
            onAddRow={handleAddResourceRow}
            onAddResources={handleAddResources}
          />
        )}
        {errors.resourceIds && (
          <div className="text-xs text-red-600 mt-2">{errors.resourceIds.message}</div>
        )}
      </SectionShell>

      {/* Notes */}
      <SectionShell title="Notes">
        <Textarea
          rows={3}
          maxLength={4000}
          showCount
          error={errors.notes?.message}
          {...register('notes')}
        />
      </SectionShell>

      {/* Recurring */}
      <SectionShell title="Recurring">
        <label className="flex items-center gap-2 text-sm mb-3">
          <Checkbox
            checked={watchedRecurring ?? false}
            onChange={(e) => setValue('recurring', e.target.checked, { shouldDirty: true })}
          />
          <span>Repeat this block</span>
        </label>

        {watchedRecurring && (
          <div className="space-y-4">
            {/* Days of week */}
            <div>
              <div className="text-xs font-medium text-slate-600 mb-2">Days of week</div>
              <div className="flex flex-wrap gap-2">
                {DAY_OPTIONS.map((d) => {
                  const key = d.key as DayKey;
                  const checked = watch(`days.${key}` as const);
                  if (readOnly) {
                    return (
                      <span
                        key={d.key}
                        data-checked={checked ? 'true' : undefined}
                        className={
                          'px-3 py-1 rounded-full border text-xs font-medium ' +
                          (checked ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-700 border-slate-300')
                        }
                      >
                        {d.label}
                      </span>
                    );
                  }
                  return (
                    <button
                      type="button"
                      key={d.key}
                      onClick={() =>
                        setValue(`days.${key}` as const, !checked, { shouldDirty: true })
                      }
                      className={
                        'px-3 py-1 rounded-full border text-xs font-medium ' +
                        (checked
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50')
                      }
                    >
                      {d.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Timeframe radio */}
            <div>
              <div className="text-xs font-medium text-slate-600 mb-2">Timeframe</div>
              <div className="space-y-2">
                {TIMEFRAME_OPTIONS.map((opt) => (
                  <label key={opt.value} className="flex items-center gap-2 text-sm">
                    <input
                      type="radio"
                      name="timeframeType"
                      value={opt.value}
                      checked={watchedTimeframeType === opt.value}
                      readOnly={readOnly}
                      onChange={
                        readOnly ? undefined : () => setValue('timeframeType', opt.value, { shouldDirty: true })
                      }
                    />
                    <span>{opt.label}</span>
                    {opt.value === 2 && watchedTimeframeType === 2 && (
                      <Input
                        type="number"
                        min={1}
                        wrapperClassName="ml-2 w-24"
                        error={errors.timeframeDuration?.message}
                        {...register('timeframeDuration', { valueAsNumber: true })}
                      />
                    )}
                    {opt.value === 3 && watchedTimeframeType === 3 && (
                      <Controller
                        control={control}
                        name="timeframeDate"
                        render={({ field }) => (
                          <DatePicker
                            wrapperClassName="ml-2"
                            value={field.value ?? ''}
                            onChange={field.onChange}
                            error={errors.timeframeDate?.message}
                          />
                        )}
                      />
                    )}
                  </label>
                ))}
              </div>
            </div>

            {/* Allowances */}
            <div>
              <div className="text-xs font-medium text-slate-600 mb-2">
                Allowances (extra dates the block applies on)
              </div>
              {readOnly ? (
                (watchedExtras ?? []).length === 0 && <p className="text-sm text-slate-500">None</p>
              ) : (
                <div className="flex items-center gap-2">
                  <DatePicker
                    wrapperClassName="flex-1"
                    value={extraDateInput}
                    onChange={setExtraDateInput}
                  />
                  <Button type="button" variant="secondary" onClick={addExtraDate}>
                    <Plus className="h-3 w-3 mr-1" />
                    Add
                  </Button>
                </div>
              )}
              {(watchedExtras ?? []).length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {(watchedExtras ?? []).map((d) => (
                    <Badge key={d} variant="info" className="pl-2 pr-1 flex items-center gap-1">
                      {d}
                      {!readOnly && (
                        <button
                          type="button"
                          className="hover:text-red-600"
                          onClick={() => removeExtraDate(d)}
                          aria-label={`Remove ${d}`}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      )}
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Exceptions */}
            <div>
              <div className="text-xs font-medium text-slate-600 mb-2">
                Exceptions (dates to skip)
              </div>
              {readOnly ? (
                (watchedExceptions ?? []).length === 0 && <p className="text-sm text-slate-500">None</p>
              ) : (
                <div className="flex items-center gap-2">
                  <DatePicker
                    wrapperClassName="flex-1"
                    value={exceptionDateInput}
                    onChange={setExceptionDateInput}
                  />
                  <Button type="button" variant="secondary" onClick={addExceptionDate}>
                    <Plus className="h-3 w-3 mr-1" />
                    Add
                  </Button>
                </div>
              )}
              {(watchedExceptions ?? []).length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {(watchedExceptions ?? []).map((d) => (
                    <Badge key={d} variant="warning" className="pl-2 pr-1 flex items-center gap-1">
                      {d}
                      {!readOnly && (
                        <button
                          type="button"
                          className="hover:text-red-600"
                          onClick={() => removeExceptionDate(d)}
                          aria-label={`Remove ${d}`}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      )}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </SectionShell>
      </FieldLockScope>

      {/* Block History */}
      {isEdit && (
        <SectionShell title="Block History">
          {historyQuery.isLoading ? (
            <div className="text-sm text-slate-500">Loading history…</div>
          ) : historyQuery.isError ? (
            <div className="text-sm text-red-600">Couldn't load history. Please try again.</div>
          ) : historyQuery.data && historyQuery.data.length > 0 ? (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {historyQuery.data.map((entry: BlockHistoryEntry, idx) => (
                <div
                  key={`${entry.blockId}-${idx}`}
                  className="text-xs border border-slate-200 rounded p-2 bg-slate-50"
                >
                  <div className="font-semibold text-slate-700">
                    {entry.auditEndDate ? 'Edited' : 'Created'}
                    {entry.auditStartDate && (
                      <> on {new Date(entry.auditStartDate).toLocaleString()}</>
                    )}
                    {entry.userFullName && (
                      <>
                        {' '}by {entry.userFullName}
                        {entry.userName && <span className="text-slate-500"> ({entry.userName})</span>}
                      </>
                    )}
                  </div>
                  <div className="text-slate-600 mt-1">
                    {entry.startTime && <>Start: {new Date(entry.startTime).toLocaleString()} · </>}
                    {entry.endTime && <>End: {new Date(entry.endTime).toLocaleString()}</>}
                  </div>
                  {entry.auditChangeReason && (
                    <div className="text-slate-500 mt-1">{entry.auditChangeReason}</div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-sm text-slate-500">No history yet.</div>
          )}
        </SectionShell>
      )}
    </Modal>
  );
}
